import Component from '@ember/component';
import { run } from '@ember/runloop';

/**
## OutsideClickDetector

Provides click detection useful for toggling UI, similar to functionality within `sq-dropdown`,
but the only DOM rendered by this component is an unstyled `div`. This was useful for working around a bug where nesting
multiple `sq-dropdown` confused Popper.js because of the styles they use.

@hbs
<OutsideClickDetector @selector=".my-selector"
                      @outsideClick={{action (mut isOpen) false}}>
  ...
</OutsideClickDetector>
```

### Parameters
 * @param {String} [selector] optional selector to scope click detection
 * @param {Action} [outsideClick] Action sent when a user clicks outside the given selector, or outside this component's `div` if no selector is passed

*/
export default class OutsideClickDetector extends Component {
  init(...args) {
    super.init(...args);
    this.runApplicationClick = (...clickArgs) => {
      run(() => this.applicationClick(...clickArgs));
    };
  }
  didInsertElement(...args) {
    super.didInsertElement(...args);
    this.bindApplicationClick();
  }
  willDestroyElement(...args) {
    super.willDestroyElement(...args);
    this.unbindApplicationClick();
  }
  bindApplicationClick() {
    this.clickListener = document.addEventListener('click', this.runApplicationClick);
    this.touchendListener = document.addEventListener('touchend', this.runApplicationClick);
  }
  unbindApplicationClick() {
    document.removeEventListener('click', this.runApplicationClick);
    document.removeEventListener('touchend', this.runApplicationClick);
  }
  applicationClick(e) {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    const selector = this.selector || `#${this.elementId}`;
    if (e.target.closest(selector)) {
      // click was inside an element matching this.selector
      return;
    }
    this.outsideClick();
  }
}